import { Injectable } from '@angular/core';
import { HttpResponseBase, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { DBkeys } from './db-keys';
import { IdToken, LoginResponse } from '../models/login-response.model';
import { User } from '../models/user.model';
import { JwtHelper } from './jwt-helper';


@Injectable()
export class Utilities {

  public static readonly captionAndMessageSeparator = ':';
  public static readonly noNetworkMessageCaption = 'No Network';
  public static readonly noNetworkMessageDetail = 'The server cannot be reached';
  public static readonly accessDeniedMessageCaption = 'Access Denied!';
  public static readonly accessDeniedMessageDetail = '';

  public static getCorrelationId(response: HttpResponseBase) {
    return response.headers.get("x-correlation-id");
  }

  public static showError(error: any, correlationId: string) {
    var errorMessage = "Fatal Error!\nAn unresolved error has occured.";

    if (error && error["message"] != undefined && error.message.length > 0)
      errorMessage += "\n\nError: " + error.message;
    else
      errorMessage += "\n\nError: " + error;

    if (correlationId)
      errorMessage += "\n\nCorrelationId: " + correlationId;

    console.error(errorMessage);
    //alert(errorMessage);
  }

  public static cookies = {
    getItem: (sKey: string) => {
      return decodeURIComponent(document.cookie.replace(new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(sKey)
        .replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1')) || null;
    },
    setItem: (sKey: string, sValue: string, vEnd: number, sPath: string, sDomain: string, bSecure: string) => {
      if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
        return false;
      }

      let sExpires = '';

      if (vEnd) {
        switch (vEnd.constructor) {
          case Number:
            sExpires = vEnd === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; max-age=' + vEnd;
            break;
          case String:
            sExpires = '; expires=' + vEnd;
            break;
          case Date:
            sExpires = '; expires=' + new Date(vEnd).toUTCString();
            break;
        }
      }

      document.cookie = encodeURIComponent(sKey) + '=' + encodeURIComponent(sValue) + sExpires + (sDomain ? '; domain=' + sDomain : '') +
        (sPath ? '; path=' + sPath : '') + (bSecure ? '; secure' : '');

      return true;
    },
    removeItem: (sKey, sPath, sDomain) => {
      if (!sKey) {
        return false;
      }
      document.cookie = encodeURIComponent(sKey) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' + (sDomain ? '; domain=' + sDomain : '') +
        (sPath ? '; path=' + sPath : '');
      return true;
    },
    hasItem: (sKey) => {
      return (new RegExp('(?:^|;\\s*)' + encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=')).test(document.cookie);
    },
    keys: () => {
      const aKeys = document.cookie.replace(/((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g, '').split(/\s*(?:\=[^;]*)?;\s*/);
      for (let nIdx = 0; nIdx < aKeys.length; nIdx++) { aKeys[nIdx] = decodeURIComponent(aKeys[nIdx]); }
      return aKeys;
    }
  };

  public static getHttpResponseMessage(data: HttpResponseBase | any): string[] {

    const responses: string[] = [];

    if (data instanceof HttpResponseBase) {

      if (this.checkNoNetwork(data)) {
        responses.push(`${this.noNetworkMessageCaption}${this.captionAndMessageSeparator} ${this.noNetworkMessageDetail}`);
      }
      else {
        const responseObject = this.getResponseBody(data);

        if (responseObject && (typeof responseObject === 'object' || responseObject instanceof Object)) {

          for (const key in responseObject) {
            if (key)
              responses.push(`${key}${this.captionAndMessageSeparator} ${responseObject[key]}`);
            else if (responseObject[key])
              responses.push(responseObject[key].toString());
          }
        }
      }

      if (!responses.length && this.getResponseBody(data))
        responses.push(`${data.statusText}: ${this.getResponseBody(data).toString()}`);
    }

    if (!responses.length)
      responses.push(data.toString());

    if (this.checkAccessDenied(data))
      responses.splice(0, 0, `${this.accessDeniedMessageCaption}${this.captionAndMessageSeparator} ${this.accessDeniedMessageDetail}`);


    return responses;
  }

  public static getResponseBody(response: HttpResponseBase) {
    if (response instanceof HttpResponse)
      return response.body;

    if (response instanceof HttpErrorResponse)
      return response.error || response.message || response.statusText;
  }

  public static checkNoNetwork(response: HttpResponseBase) {
    if (response instanceof HttpResponseBase) {
      return response.status == 0;
    }

    return false;
  }

  public static checkAccessDenied(response: HttpResponseBase) {
    if (response instanceof HttpResponseBase) {
      return response.status == 403;
    }

    return false;
  }

  public static checkNotFound(response: HttpResponseBase) {
    if (response instanceof HttpResponseBase) {
      return response.status == 404;
    }

    return false;
  }

  public static getQueryParamsFromString(paramString: string) {

    if (!paramString)
      return null;

    const params: { [key: string]: string } = {};

    for (const param of paramString.split('&')) {
      const keyValue = Utilities.splitInTwo(param, '=');
      params[keyValue.firstPart] = keyValue.secondPart;
    }

    return params;
  }

  public static splitInTwo(text: string, separator: string): { firstPart: string, secondPart: string } {
    const separatorIndex = text.indexOf(separator);

    if (separatorIndex == -1)
      return { firstPart: text, secondPart: null };

    const part1 = text.substr(0, separatorIndex).trim();
    const part2 = text.substr(separatorIndex + 1).trim();

    return { firstPart: part1, secondPart: part2 };
  }

  public static safeStringify(object) {

    let result: string;

    try {
      result = JSON.stringify(object);
      return result;
    }
    catch (error) {

    }

    const simpleObject = {};

    for (const prop in object) {
      if (!object.hasOwnProperty(prop)) {
        continue;
      }
      if (typeof (object[prop]) == 'object') {
        continue;
      }
      if (typeof (object[prop]) == 'function') {
        continue;
      }
      simpleObject[prop] = object[prop];
    }

    result = '[***Sanitized Object***]: ' + JSON.stringify(simpleObject);

    return result;
  }

  public static JSonTryParse(value: string) {
    try {
      return JSON.parse(value);
    }
    catch (e) {
      if (value === 'undefined')
        return void 0;

      return value;
    }
  }

  public static TestIsObjectEmpty(obj: any) {
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }

    return true;
  }

  public static TestIsUndefined(value: any) {
    return typeof value === 'undefined';
  }

  public static TestIsString(value: any) {
    return typeof value === 'string' || value instanceof String;
  }

  public static capitalizeFirstLetter(text: string) {
    if (text)
      return text.charAt(0).toUpperCase() + text.slice(1);
    else
      return text;
  }

  public static toTitleCase(text: string) {
    return text.replace(/\w\S*/g, (subString) => {
      return subString.charAt(0).toUpperCase() + subString.substr(1).toLowerCase();
    });
  }

  public static toLowerCase(items: string);
  public static toLowerCase(items: string[]);
  public static toLowerCase(items: any): string | string[] {

    if (items instanceof Array) {
      const loweredRoles: string[] = [];

      for (let i = 0; i < items.length; i++) {
        loweredRoles[i] = items[i].toLowerCase();
      }

      return loweredRoles;
    }
    else if (typeof items === 'string' || items instanceof String) {
      return items.toLowerCase();
    }
  }

  public static uniqueId() {
    return this.randomNumber(1000000, 9000000).toString();
  }

  public static randomNumber(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  public static baseUrl() {
    let base = '';

    if (window.location.origin)
      base = window.location.origin;
    else
      base = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');

    return base.replace(/\/$/, '');
  }

  public static searchArray(searchTerm: string, caseSensitive: boolean, ...values: any[]) {

    if (!searchTerm)
      return true;


    if (!caseSensitive)
      searchTerm = searchTerm.toLowerCase();

    for (const value of values) {

      if (value != null) {
        let strValue = value.toString();

        if (!caseSensitive)
          strValue = strValue.toLowerCase();

        if (strValue.indexOf(searchTerm) !== -1)
          return true;
      }
    }

    return false;
  }

  public static moveArrayItem(array: any[], oldIndex, newIndex) {

    while (oldIndex < 0) {
      oldIndex += this.length;
    }

    while (newIndex < 0) {
      newIndex += this.length;
    }

    if (newIndex >= this.length) {
      let k = newIndex - this.length;
      while ((k--) + 1) {
        array.push(undefined);
      }
    }

    array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
  }

  public static expandCamelCase(text: string) {

    if (!text)
      return text;

    return text.replace(/([A-Z][a-z]+)/g, ' $1')
      .replace(/([A-Z][A-Z]+)/g, ' $1')
      .replace(/([^A-Za-z ]+)/g, ' $1');
  }

  // public static removeNulls(obj) {
  //   const isArray = obj instanceof Array;

  //   for (const k in obj) {
  //     if (obj.hasOwnProperty(k)) {
  //       if (obj[k] === null) {
  //         isArray ? obj.splice(k, 1) : delete obj[k];
  //       }
  //       else if (typeof obj[k] == 'object') {
  //         Utilities.removeNulls(obj[k]);
  //       }

  //       if (isArray && obj.length == k) {
  //         Utilities.removeNulls(obj);
  //       }
  //     }
  //   }

  //   return obj;
  // }

  public static saveUserDetails(info: LoginResponse, user: User) {
    localStorage.setItem(DBkeys.ACCESS_TOKEN, info.access_token);
    localStorage.setItem(DBkeys.REFRESH_TOKEN, info.refresh_token);
    localStorage.setItem(DBkeys.TOKEN_EXPIRES_IN, info.expires_in.toString());
    localStorage.setItem(DBkeys.CURRENT_USER, JSON.stringify(user));
  }

  public static saveDelegationUserDetails(user: User, accessToken: string, refreshToken: string, expiresIn: Date) {
    sessionStorage.setItem(DBkeys.DELEGATION_ACCESS_TOKEN, accessToken);
    sessionStorage.setItem(DBkeys.DELEGATION_REFRESH_TOKEN, refreshToken);
    sessionStorage.setItem(DBkeys.DELEGATION_TOKEN_EXPIRES_IN, expiresIn.toString());
    sessionStorage.setItem(DBkeys.DELEGATION_USER, JSON.stringify(user));
  }

  public static processLogout() {
    localStorage.removeItem(DBkeys.ACCESS_TOKEN);
    localStorage.removeItem(DBkeys.REFRESH_TOKEN);
    localStorage.removeItem(DBkeys.TOKEN_EXPIRES_IN);
    localStorage.removeItem(DBkeys.CURRENT_USER);
  }

  public static processDelegationLogout() {
    sessionStorage.removeItem(DBkeys.DELEGATION_ACCESS_TOKEN);
    sessionStorage.removeItem(DBkeys.DELEGATION_REFRESH_TOKEN);
    sessionStorage.removeItem(DBkeys.DELEGATION_TOKEN_EXPIRES_IN);
    sessionStorage.removeItem(DBkeys.DELEGATION_USER);
  }

  public static processLoginResponse(info: LoginResponse) {

    const accessToken = info.access_token;

    if (accessToken == null)
      throw new Error('Received access_token was empty');

    const jwtHelper = new JwtHelper();
    const decodedIdToken = <IdToken>jwtHelper.decodeToken(accessToken);

    const user = new User(decodedIdToken.sub, decodedIdToken.email, decodedIdToken.name, decodedIdToken.surname, decodedIdToken.fullname, decodedIdToken.role);
    user.isEnabled = true;

    Utilities.saveUserDetails(info, user);

    return user;
  }
}
